import { DecisionCriteriaMismatch } from "@eave/shared/types/api";
import { ReoOwnershipStatuses } from "./owned-real-estate";

// TODO: jjw: put field keys in enums & map to display values

export interface SandboxRequestPercentOrValue {
  percent: number | null;
  value: number | null;
}

export interface SandboxRequestREO {
  id: number;
  ownership_status: ReoOwnershipStatuses;
}

export interface SandboxReworkRequest {
  description: string;
  sandbox_request: SandboxRequest;
  sandbox_response: SandboxResponse;
}

export interface SandboxRequest {
  loan_amount: number;
  downpayment: number;
  appraised_value: number;
  property_type: string;
  property_taxes: SandboxRequestPercentOrValue;
  homeowners_insurance: SandboxRequestPercentOrValue;
  hoa: SandboxRequestPercentOrValue;
  supplemental_property_insurance: SandboxRequestPercentOrValue;
  mortgage_insurance: SandboxRequestPercentOrValue;
  price_exception: number;
  decision_criteria: string;
  monthly_income: number;
  monthly_liabilities: number;
  total_liabilities_excluded_from_dti: number;
  total_assets: number;
  total_assets_for_reserves: number;
  reos: SandboxRequestREO[];
  full_entitlement: boolean;
  used_entitlement_amount: number;
  va_finance_funding_fee: boolean;
  va_funding_fee_to_finance: number;
}

const parsePercentOrValue = (obj: any): SandboxRequestPercentOrValue => ({
  ...obj,
  percent: obj.percent === null ? null : Number(obj.percent),
  value: obj.value === null ? null : Number(obj.value),
});

const parseReo = (obj: any): SandboxRequestREO => ({
  ...obj,
  id: Number(obj.id),
});

/** For taking SandboxRequests emitted by the API and parsing any decimal values from strings. */
export const parseSandboxRequest = (req: any): SandboxRequest => ({
  ...req,
  loan_amount: Number(req.loan_amount),
  downpayment: Number(req.downpayment),
  appraised_value: Number(req.appraised_value),
  property_taxes: parsePercentOrValue(req.property_taxes),
  homeowners_insurance: parsePercentOrValue(req.homeowners_insurance),
  hoa: parsePercentOrValue(req.hoa),
  supplemental_property_insurance: parsePercentOrValue(req.supplemental_property_insurance),
  mortgage_insurance: parsePercentOrValue(req.mortgage_insurance),
  price_exception: Number(req.price_exception),
  monthly_income: Number(req.monthly_income),
  monthly_liabilities: Number(req.monthly_liabilities),
  total_liabilities_excluded_from_dti: Number(req.total_liabilities_excluded_from_dti),
  total_assets: Number(req.total_assets),
  total_assets_for_reserves: Number(req.total_assets_for_reserves),
  reos: req.reos.map(parseReo),
  used_entitlement_amount: Number(req.used_entitlement_amount),
  va_funding_fee_to_finance: Number(req.va_funding_fee_to_finance),
});

export interface SandboxInputsForREOStatusesREO {
  id: number;
  assets: number;
  income: number;
  liabilities: number;
}
export interface SandboxInputsForREOStatuses {
  total_assets: number;
  monthly_liabilities: number;
  monthly_income: number;
  reos: SandboxInputsForREOStatusesREO[];
}

const parseSandboxInputsForReoStatusesReo = (obj: any): SandboxInputsForREOStatusesREO => ({
  ...obj,
  id: Number(obj.id),
  assets: Number(obj.assets),
  income: Number(obj.income),
  liabilities: Number(obj.liabilities),
});

export const parseSandboxInputsForReoStatuses = (inputs: any): SandboxInputsForREOStatuses => ({
  ...inputs,
  total_assets: Number(inputs.total_assets),
  monthly_liabilities: Number(inputs.monthly_liabilities),
  monthly_income: Number(inputs.monthly_income),
  reos: inputs.reos.map(parseSandboxInputsForReoStatusesReo),
});

export interface SandboxResponseLoanDetails {
  loan_type: string;
  decision_criteria: string;
  rate: number;
  qualification_rate: number;
  apr: number;
}

export interface SandboxResponseMortgageMaximums {
  max_purchase_price: number;
  max_loan_amount: number;
  max_downpayment: number;
  ltv_at_max: number;
  pitia_at_max: number;
  dti_at_max: number;
  max_dti: number;
  eligible_ltv_at_max: {
    original: number;
    override: number | null;
  };
  max_ltv: number;
  reserves: number;
}

export interface SandboxResponseCashOfferAffordability {
  max_purchase_price: number;
  max_loan_amount: number;
  max_ltv: number;
  min_appraisal_for_cash_offer_loan_amount: number;
  appraisal_wiggle_room_at_max: number;
  min_appraisal_for_purchase_price: number;
  appraisal_wiggle_room_for_funds: number;
  appraisal_wiggle_room_percent: {
    percentage: number;
    purchase_price: number;
  };
}

export interface SandboxResponseProgram {
  name: string;
  loan_details: SandboxResponseLoanDetails;
  mortgage_maximums: SandboxResponseMortgageMaximums;
  cash_offer_affordability: SandboxResponseCashOfferAffordability;
}

export interface SandboxResponse {
  programs: SandboxResponseProgram[];
  error: null | DecisionCriteriaMismatch;
}

/** For taking SandboxResponses emitted by the API and parsing any decimal values from strings. */
export const parseSandboxResponse = (resp: any): SandboxResponse => ({
  error: resp.error,
  programs: resp.programs.map((program: any) => {
    const { loan_details, mortgage_maximums, cash_offer_affordability } = program;

    return {
      ...program,
      loan_details: {
        ...loan_details,
        rate: Number(loan_details.rate),
        qualification_rate: Number(loan_details.qualification_rate),
        apr: Number(loan_details.apr),
      },
      mortgage_maximums: {
        ...mortgage_maximums,
        max_purchase_price: Number(mortgage_maximums.max_purchase_price),
        max_loan_amount: Number(mortgage_maximums.max_loan_amount),
        max_downpayment: Number(mortgage_maximums.max_downpayment),
        ltv_at_max: Number(mortgage_maximums.ltv_at_max),
        pitia_at_max: Number(mortgage_maximums.pitia_at_max),
        dti_at_max: Number(mortgage_maximums.dti_at_max),
        max_dti: Number(mortgage_maximums.max_dti),
        eligible_ltv_at_max: {
          ...mortgage_maximums.eligible_ltv_at_max,
          original: Number(mortgage_maximums.eligible_ltv_at_max.original),
          override:
            mortgage_maximums.eligible_ltv_at_max.override === null
              ? null
              : Number(mortgage_maximums.eligible_ltv_at_max.override),
        },
        max_ltv: Number(mortgage_maximums.max_ltv),
        reserves: Number(mortgage_maximums.reserves),
      },
      cash_offer_affordability: {
        ...cash_offer_affordability,
        max_purchase_price: Number(cash_offer_affordability.max_purchase_price),
        max_loan_amount: Number(cash_offer_affordability.max_loan_amount),
        max_ltv: Number(cash_offer_affordability.max_ltv),
        min_appraisal_for_cash_offer_loan_amount: Number(
          cash_offer_affordability.min_appraisal_for_cash_offer_loan_amount,
        ),
        appraisal_wiggle_room_at_max: Number(cash_offer_affordability.appraisal_wiggle_room_at_max),
        min_appraisal_for_purchase_price: Number(
          cash_offer_affordability.min_appraisal_for_purchase_price,
        ),
        appraisal_wiggle_room_for_funds: Number(
          cash_offer_affordability.appraisal_wiggle_room_for_funds,
        ),
        appraisal_wiggle_room_percent: {
          ...cash_offer_affordability.appraisal_wiggle_room_percent,
          percentage: Number(cash_offer_affordability.appraisal_wiggle_room_percent.percentage),
          purchase_price: Number(
            cash_offer_affordability.appraisal_wiggle_room_percent.purchase_price,
          ),
        },
      },
    };
  }),
});
